import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/auth'

interface OrganizationSelectorProps {
  onCreateNew?: () => void
  compact?: boolean
}

export default function OrganizationSelector({ onCreateNew, compact }: OrganizationSelectorProps) {
  const { t } = useTranslation()
  const { organizations, activeOrg, setActiveOrg } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId)
    if (org) {
      setActiveOrg(org)
      // Clear caches by reloading page to ensure fresh data
      window.location.reload()
    }
    setIsOpen(false)
  }

  // Get role badge color
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-amber-100 text-amber-800'
      case 'admin':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  // Get role label in Finnish
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Omistaja'
      case 'admin':
        return 'Ylläpitäjä'
      default:
        return 'Jäsen'
    }
  }

  if (organizations.length === 0) {
    if (compact) {
      return (
        <div className="p-2 bg-indigo-800/50 rounded-lg text-indigo-200">
          <span className="text-lg">🏢</span>
        </div>
      )
    }
    return (
      <div className="px-3 py-2 bg-indigo-800/50 rounded-lg text-indigo-200 text-sm">
        Ei organisaatioita
      </div>
    )
  }

  // Compact version for mobile header
  if (compact) {
    return (
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex items-center justify-center p-2 rounded-lg
            transition-colors duration-150 min-w-[44px] min-h-[44px]
            ${isOpen ? 'bg-indigo-700' : 'bg-indigo-800/50 hover:bg-indigo-700'}
          `}
          aria-label={activeOrg?.name || t('org.selectOrg')}
        >
          <span className="text-lg">🏢</span>
          {activeOrg && (
            <span className={`ml-1.5 w-2 h-2 rounded-full ${getRoleColor(activeOrg.role).split(' ')[0].replace('bg-', 'bg-').replace('100', '400')}`} />
          )}
        </button>

        {/* Dropdown menu for compact */}
        {isOpen && (
          <div className="absolute right-0 z-50 w-64 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto">
            <div className="py-1">
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('org.selectOrg')}
              </div>
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelect(org.id)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 text-left
                    hover:bg-gray-50 transition-colors
                    ${activeOrg?.id === org.id ? 'bg-indigo-50 border-l-3 border-indigo-600' : ''}
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">🏢</span>
                      <span className={`truncate ${activeOrg?.id === org.id ? 'font-medium text-indigo-900' : 'text-gray-700'}`}>
                        {org.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 ml-6">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(org.role)}`}>
                        {getRoleLabel(org.role)}
                      </span>
                    </div>
                  </div>
                  {activeOrg?.id === org.id && (
                    <svg className="w-5 h-5 text-indigo-600 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {onCreateNew && (
              <>
                <div className="border-t border-gray-200 my-1" />
                <button
                  onClick={() => {
                    setIsOpen(false)
                    onCreateNew()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="font-medium">{t('org.createNew')}</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3 py-2 rounded-lg text-left
          transition-colors duration-150
          ${isOpen ? 'bg-indigo-700' : 'bg-indigo-800/50 hover:bg-indigo-700'}
        `}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">🏢</span>
            <span className="font-medium text-white truncate">
              {activeOrg?.name || t('org.selectOrg')}
            </span>
          </div>
          {activeOrg && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(activeOrg.role)}`}>
                {getRoleLabel(activeOrg.role)}
              </span>
            </div>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-indigo-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto">
          {/* Organization list */}
          <div className="py-1">
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('org.selectOrg')}
            </div>
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelect(org.id)}
                className={`
                  w-full flex items-center justify-between px-3 py-2 text-left
                  hover:bg-gray-50 transition-colors
                  ${activeOrg?.id === org.id ? 'bg-indigo-50 border-l-3 border-indigo-600' : ''}
                `}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">🏢</span>
                    <span className={`truncate ${activeOrg?.id === org.id ? 'font-medium text-indigo-900' : 'text-gray-700'}`}>
                      {org.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 ml-6">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(org.role)}`}>
                      {getRoleLabel(org.role)}
                    </span>
                  </div>
                </div>
                {activeOrg?.id === org.id && (
                  <svg className="w-5 h-5 text-indigo-600 ml-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-1" />

          {/* Create new button */}
          {onCreateNew && (
            <button
              onClick={() => {
                setIsOpen(false)
                onCreateNew()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-medium">{t('org.createNewOrg')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
